import { useState } from 'react';
import { X, Trash2, Download, Save } from 'lucide-react';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';
import {
  useFrameworkTemplates,
  useSaveFrameworkTemplate,
  useDeleteFrameworkTemplate,
} from '../../hooks/useFramework';
import { api } from '../../lib/api';
import { useToast } from '../../contexts/ToastContext';
import type { FrameworkConfig } from '../../types';

interface FrameworkTemplateManagerProps {
  currentConfig: FrameworkConfig;
  onLoadTemplate: (config: FrameworkConfig) => void;
  onClose: () => void;
}

export function FrameworkTemplateManager({
  currentConfig,
  onLoadTemplate,
  onClose,
}: FrameworkTemplateManagerProps) {
  const { showToast } = useToast();
  const { data: templatesData, isLoading, error } = useFrameworkTemplates();
  const saveMutation = useSaveFrameworkTemplate();
  const deleteMutation = useDeleteFrameworkTemplate();

  const [newTemplateName, setNewTemplateName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);

  const templates = templatesData || [];

  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      showToast('Please enter a template name', 'error');
      return;
    }

    try {
      await saveMutation.mutateAsync({
        name: newTemplateName.trim(),
        config: currentConfig,
      });
      showToast(`Template "${newTemplateName}" saved`, 'success');
      setNewTemplateName('');
      setShowSaveForm(false);
    } catch {
      showToast('Failed to save template', 'error');
    }
  };

  const handleDeleteTemplate = async (name: string) => {
    if (!confirm(`Delete template "${name}"?`)) return;

    try {
      await deleteMutation.mutateAsync(name);
      showToast(`Template "${name}" deleted`, 'success');
    } catch {
      showToast('Failed to delete template', 'error');
    }
  };

  const handleLoadTemplate = async (name: string) => {
    try {
      const config = await api.getFrameworkTemplate(name);
      onLoadTemplate(config);
      showToast(`Loaded template "${name}"`, 'success');
      onClose();
    } catch {
      showToast('Failed to load template', 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-wolf-800 rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col border border-wolf-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-wolf-700">
          <h2 className="text-xl font-semibold text-white">Templates</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-wolf-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Save as template section */}
          <div className="mb-6">
            {showSaveForm ? (
              <div className="bg-wolf-900 rounded-lg p-4 border border-wolf-600">
                <label className="block text-wolf-300 text-sm mb-2">Template Name</label>
                <input
                  type="text"
                  className="w-full bg-wolf-800 border border-wolf-600 rounded px-3 py-2 text-white mb-3"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="Enter template name..."
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveAsTemplate}
                    disabled={saveMutation.isPending || !newTemplateName.trim()}
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {saveMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowSaveForm(false);
                      setNewTemplateName('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="secondary" onClick={() => setShowSaveForm(true)} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                Save Current Config as Template
              </Button>
            )}
          </div>

          {/* Templates list */}
          <div>
            <h3 className="text-wolf-300 text-sm font-medium mb-3">Available Templates</h3>

            {isLoading && (
              <div className="flex justify-center py-4">
                <LoadingSpinner message="Loading templates..." />
              </div>
            )}

            {error && (
              <p className="text-red-400 text-center py-4">Failed to load templates</p>
            )}

            {!isLoading && !error && templates.length === 0 && (
              <p className="text-wolf-500 text-center py-4 italic">No templates saved</p>
            )}

            {!isLoading && !error && templates.length > 0 && (
              <div className="space-y-2">
                {templates.map((name) => (
                  <div
                    key={name}
                    className="bg-wolf-900 rounded-lg p-3 border border-wolf-700 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white font-medium">{name}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleLoadTemplate(name)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <button
                        type="button"
                        onClick={() => handleDeleteTemplate(name)}
                        disabled={deleteMutation.isPending}
                        className="p-2 text-wolf-500 hover:text-red-400 transition-colors disabled:opacity-50"
                        title="Delete template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-wolf-700">
          <Button variant="secondary" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
